import { motion, useInView } from "framer-motion";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { staggerContainerVariants, staggerItemVariants, SPRING_BOUNCE } from "./animations";
import { isMobile } from "react-device-detect";

// Trait cards with images (similar to WhyChooseUs cards)
const traitCards = [
  {
    key: "shared_experiences",
    thumbnailSrc: "/images/leader-lead-for-impact.jpg",

    gradient:
      "linear-gradient(180deg, rgba(0, 0, 0, 0.4) 14%, rgba(0, 0, 0, 0) 28.5%, rgba(0, 0, 0, 0) 67%, rgba(0, 0, 0, 0.4) 77.26%)",
  },
  {
    key: "deep_conversations",
    thumbnailSrc: "/images/leader-deep-conversations.jpg",
    gradient:
      "linear-gradient(180deg, rgba(0, 0, 0, 0.5) 21.05%, rgba(0, 0, 0, 0) 29.88%, rgba(0, 0, 0, 0) 68.91%, rgba(0, 0, 0, 0.5) 81.27%)",
  },
  {
    key: "lead_for_impact",
    thumbnailSrc: "/images/leader-shared-experiences.jpg",
    gradient:
      "linear-gradient(180deg, rgba(0, 0, 0, 0.29) 15.56%, rgba(0, 0, 0, 0) 23.15%, rgba(0, 0, 0, 0) 69.8%, rgba(0, 0, 0, 0.58) 74.58%)",
  },
  {
    key: "travel_transformation",
    thumbnailSrc: "/images/leader-travel-transformation.jpg",
    gradient:
      "linear-gradient(180deg, rgba(0, 0, 0, 0.312) 18.89%, rgba(0, 0, 0, 0) 33.94%, rgba(0, 0, 0, 0) 53.36%, rgba(0, 0, 0, 0.468) 75.46%)",
  },
];

const TraitCard = ({ title, description, thumbnailSrc, gradient }) => {
  return (
    <div className="tw-relative tw-h-[400px] tw-rounded-3xl tw-drop-shadow-[0px_4px_4px_0px_#00000040]">
      <img
        alt="trait"
        className="tw-h-[400px] tw-w-full tw-rounded-3xl tw-object-cover"
        src={thumbnailSrc}
      />
      <div className="tw-absolute tw-inset-0 tw-rounded-3xl" style={{ background: gradient }} />
      <div className="tw-absolute tw-left-4 tw-right-4 tw-top-6 md:tw-top-8">
        <div
          className="tw-text-center tw-text-2xl tw-font-extrabold tw-text-white md:tw-text-3xl"
          style={{ textShadow: "1px 1px 3px rgba(0,0,0,0.7)" }}
        >
          {title}
        </div>
      </div>
      <div className="tw-absolute tw-bottom-4 tw-left-4 tw-right-4 tw-flex tw-flex-col tw-gap-2 md:tw-bottom-6">
        <div
          className="tw-text-center tw-text-sm tw-font-medium tw-text-white md:tw-text-base"
          style={{ textShadow: "1px 1px 3px rgba(0,0,0,0.7)" }}
        >
          {description}
        </div>
      </div>
    </div>
  );
};

const WhyAndWho = () => {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const problems = t("leader_programme.why_and_who.problems", { returnObjects: true }) || [];

  return (
    <section
      ref={sectionRef}
      className="tw-flex tw-snap-start tw-flex-col tw-justify-center tw-bg-white tw-py-16 md:tw-py-20"
    >
      <div className="tw-max-w-8xl tw-mx-auto tw-px-6 md:tw-px-12 lg:tw-px-16">
        {/* Who This Is For - Card grid like WhyChooseUs */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ ...SPRING_BOUNCE, delay: 0.2 }}
        >
          <div className="tw-mb-2 tw-text-center tw-text-[26px] tw-font-extrabold md:tw-text-[32px]">
            {t("leader_programme.why_and_who.who_title")}
          </div>
          <div
            style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            className="tw-mb-8 tw-px-2 tw-text-center tw-text-base tw-font-semibold tw-text-primary-main md:tw-mb-12 md:tw-px-0 md:tw-text-lg"
          >
            <p
              style={
                isMobile
                  ? { width: "100%" }
                  : {
                      width: "50%",
                    }
              }
            >
              {t("leader_programme.hero.subheadline")}
            </p>
          </div>

          {/* Cards Grid - Vertical stack on mobile, grid on desktop */}
          <div className="tw-flex tw-flex-col tw-gap-7 md:tw-grid md:tw-grid-cols-4 md:tw-gap-6">
            {traitCards.map((card, index) => (
              <div key={card.key} className="tw-w-full">
                <TraitCard
                  title={t(`leader_programme.why_and_who.trait_cards.${card.key}.title`)}
                  description={t(
                    `leader_programme.why_and_who.trait_cards.${card.key}.description`
                  )}
                  thumbnailSrc={card.thumbnailSrc}
                  gradient={card.gradient}
                />
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyAndWho;
